import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { ALL_DEPTH_LIMIT, VACANT_WARN_DAYS } from '../orgChartModel';

// Sağ ayrıntı paneli (spec 2026-10-24 §7). Kart sade kaldığı için bütün bilgi ve aksiyon burada:
//   • herkes: kişi, kurumsal e-posta, yöneticisi, doğrudan bağlıları, Kişi Odaklı'ya geçiş, "Alt Dalı Tamamen Aç";
//   • yönetici kapsamı (eski Ekibimin Şeması): kaç gündür boş, ayrılıyor, açık talep, Kadro Talep Et, Yeni Pozisyon İçin Talep;
//   • Düzenle (EM.Write): koltuk/iş kodu, planlanan kadro, ata/kaldır/alt ekle/kopyala/sil/üst bağını kaldır.

const Panel = styled.aside`
  width: 340px;
  flex-shrink: 0;
  min-height: 0;
  overflow-y: auto;
  box-sizing: border-box;
  background: #fff;
  border-left: 1px solid #E6EAF0;
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-family: 'Nunito Sans', sans-serif;
  color: #1F2A37;
`;

const Close = styled.button`
  align-self: flex-end;
  margin: -8px -8px 0 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;
  display: flex;
  padding: 4px;
  border-radius: 6px;
  &:hover { background: #F5F7FA; }
`;

const Head = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
`;

const Avatar = styled.div`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 800;
  font-size: ${({ $size }) => Math.round($size / 3)}px;
  background: ${({ $color }) => $color};
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const Name = styled.h2`
  margin: 0;
  font-size: 19px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Sub = styled.div`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 13px;
  margin-top: 2px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 999px;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => `${$color}1f`};
`;

const You = styled.span`
  font-size: 10px;
  font-weight: 800;
  background: ${({ theme }) => theme.colors.primary.main};
  color: #fff;
  border-radius: 5px;
  padding: 1px 6px;
`;

const Kv = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 8px 10px;
  font-size: 13px;
  span:nth-child(odd) { color: ${({ theme }) => theme.colors.text.muted}; }
  span { overflow-wrap: anywhere; }
`;

const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: #A0A8B4;
  text-transform: uppercase;
  margin-top: 6px;
`;

const PersonRow = styled.button`
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
  padding: 8px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: 13px 'Nunito Sans', sans-serif;
  color: inherit;
  &:hover { background: #F5F7FA; }
  small { display: block; color: ${({ theme }) => theme.colors.text.muted}; }
`;

const Badges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Badge = styled.span`
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

const LinkBadge = styled.button`
  font: 700 11px 'Nunito Sans', sans-serif;
  padding: 2px 8px;
  border-radius: 6px;
  border: 0;
  background: #EFF6FF;
  color: #2563EB;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

function initials(name) {
  return (name || '').split(' ').filter(Boolean).slice(0, 2).map((s) => s[0]).join('').toLocaleUpperCase('tr') || '?';
}

function Person({ data, size, color }) {
  return (
    <Avatar $size={size} $color={data?.isAssigned === false ? '#C3CBD5' : color}>
      {data?.isAssigned !== false && data?.profilePicture
        ? <img src={data.profilePicture} alt="" />
        : (data?.isAssigned === false ? '?' : initials(data?.userName))}
    </Avatar>
  );
}

export default function OrgDetailsPanel({
  node, manager, directReports, variant = 'view', color, colorOf = () => color, perms = {}, expandAllBlocked = false,
  reportsLoading = false, expandAllLoading = false,
  onClose, onSelect, onLoadReports, onOpenPerson, onExpandAll, onRequestHire, onRequestNewPosition, onOpenRequisition,
  onToggleGroup, onAssign, onUnassign, onAddChild, onDuplicate, onDeletePosition, onDetach,
}) {
  const { t } = useTranslation();

  useEffect(() => {
    // Esc yalnız panele aitse kapatır: açık bir diyalog/çekmece ya da yazı alanı (arama listesi) Esc'i kendisi kullanır.
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      if (document.querySelector('[role="dialog"], .MuiModal-root')) return;
      onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!node) return null;
  const isVacant = !node.isAssigned;
  const direct = node.directCount || 0;
  const label = (d) => (d?.isAssigned === false ? t('orgChart.vacantPosition') : (d?.userName || '-'));
  const scoped = Boolean(node.inManagedScope);
  const showScope = variant !== 'view' && (scoped || perms.canRequestNewPosition || node.groupKey);
  const isEdit = variant === 'edit';
  const locked = Boolean(perms.locked);
  const expandDisabled = Boolean(node.allDepthTooLarge) || expandAllBlocked;
  const vacantWarn = typeof node.vacantSinceDays === 'number' && node.vacantSinceDays > VACANT_WARN_DAYS;
  const requisitionText = scoped && node.openRequisitionId
    ? t('orgChart.scope.openRequisition', {
      status: node.openRequisitionStatus
        ? t(`ats.requisition.status.${node.openRequisitionStatus}`, { defaultValue: node.openRequisitionStatus })
        : '',
    })
    : null;
  const row = (d, key) => (
    <PersonRow key={key} type="button" data-testid={`panel-person-${d.positionId}`} onClick={() => onSelect?.(String(d.positionId))}>
      <Person data={d} size={28} color={colorOf(d)} />
      <span><b>{label(d)}</b><small>{d.titleName || '-'}</small></span>
    </PersonRow>
  );

  return (
    <Panel data-testid="org-details-panel" aria-label={label(node)}>
      <Close type="button" data-testid="panel-close" aria-label={t('common.close')} onClick={() => onClose?.()}>
        <X size={18} />
      </Close>

      <Head>
        <Person data={node} size={72} color={color} />
        <div style={{ minWidth: 0 }}>
          <Name>
            {label(node)}
            {node.isOwn && <You>{t('orgChart.you')}</You>}
          </Name>
          <Sub>{node.titleName || '-'}</Sub>
          {node.departmentName && <Chip $color={color} data-testid="panel-department">{node.departmentName}</Chip>}
        </div>
      </Head>

      <Kv>
        {!isVacant && node.corporateEmail && (
          <>
            <span>{t('orgChart.email')}</span>
            <span data-testid="panel-email">{node.corporateEmail}</span>
          </>
        )}
        <span>{t('orgChart.directCount')}</span>
        <span>{direct}</span>
        {typeof node.totalHeadcount === 'number' && (
          <>
            <span>{t('orgChart.totalTeam')}</span>
            <span>{node.totalHeadcount}</span>
          </>
        )}
      </Kv>

      {manager && (
        <>
          <SectionTitle>{t('orgChart.sectionManager')}</SectionTitle>
          {row(manager, 'manager')}
        </>
      )}

      {direct > 0 && (
        <>
          <SectionTitle>{t('orgChart.sectionReports')}</SectionTitle>
          {directReports
            ? directReports.map((d) => row(d, d.positionId))
            : (
              <Button variant="ghost" data-testid="panel-show-reports" loading={reportsLoading}
                onClick={() => onLoadReports?.(String(node.positionId))}>
                {t('orgChart.showReports')}
              </Button>
            )}
        </>
      )}

      <Actions>
        <Button data-testid="panel-open-person" onClick={() => onOpenPerson?.(String(node.positionId))}>
          {t('orgChart.openPerson')}
        </Button>
        {direct > 0 && (
          <span title={expandDisabled ? t('orgChart.depthAllDisabled', { limit: ALL_DEPTH_LIMIT }) : undefined}>
            <Button variant="outline" fullWidth data-testid="panel-expand-all" disabled={expandDisabled}
              loading={expandAllLoading} onClick={() => onExpandAll?.(String(node.positionId))}>
              {t('orgChart.expandAll')}
            </Button>
          </span>
        )}
        {expandDisabled && direct > 0 && (
          <Hint data-testid="panel-expand-all-hint">{t('orgChart.depthAllDisabled', { limit: ALL_DEPTH_LIMIT })}</Hint>
        )}
      </Actions>

      {showScope && (
        <>
          <SectionTitle>{t('orgChart.sectionManagerTools')}</SectionTitle>
          {scoped && (
            <Badges>
              {isVacant && typeof node.vacantSinceDays === 'number' && (
                <Badge data-testid="panel-vacant-days" data-warn={vacantWarn ? 'true' : 'false'}
                  $bg={vacantWarn ? '#FDECEA' : '#F0F2F5'} $color={vacantWarn ? '#E53935' : '#6B7280'}>
                  {t('orgChart.scope.vacantForDays', { count: node.vacantSinceDays })}
                </Badge>
              )}
              {!isVacant && node.occupantLeaving && (
                <Badge data-testid="panel-leaving" $bg="#FEF3C7" $color="#B45309">{t('orgChart.scope.leaving')}</Badge>
              )}
              {requisitionText && (perms.canOpenRequisition ? (
                <LinkBadge type="button" data-testid="panel-open-requisition-link"
                  onClick={() => onOpenRequisition?.(node.openRequisitionId)}>
                  {requisitionText}
                </LinkBadge>
              ) : (
                <Badge data-testid="panel-open-requisition-badge" $bg="#EFF6FF" $color="#2563EB">{requisitionText}</Badge>
              ))}
            </Badges>
          )}
          <Actions>
            {scoped && perms.canRequestHire && !node.openRequisitionId && (
              <Button data-testid="panel-request-hire" onClick={() => onRequestHire?.(String(node.positionId))}>
                {t('orgChart.scope.requestHire')}
              </Button>
            )}
            {perms.canRequestNewPosition && (
              <Button variant="outline" data-testid="panel-request-new-position"
                onClick={() => onRequestNewPosition?.(String(node.positionId))}>
                {t('orgChart.scope.requestNewPosition')}
              </Button>
            )}
            {node.groupKey && (
              <Button variant="ghost" data-testid="panel-collapse-group" onClick={() => onToggleGroup?.(node.groupKey)}>
                {t('orgChart.scope.collapseGroup')}
              </Button>
            )}
          </Actions>
        </>
      )}

      {isEdit && (
        <>
          <SectionTitle>{t('orgChart.sectionPosition')}</SectionTitle>
          <Kv data-testid="panel-position">
            <span>{t('orgChart.seatCode')}</span>
            <span>{node.code || '-'}</span>
            <span>{t('orgChart.jobCode')}</span>
            <span>{node.titleCode || '-'}</span>
            <span>{t('jobGrade.grade')}</span>
            <span data-testid="panel-grade">{node.gradeName || '-'}</span>
            <span>{t('jobFamily.jobFamily')}</span>
            <span data-testid="panel-job-family">{node.jobFamilyName || '-'}</span>
            <span>{t('orgChart.plannedHeadcount')}</span>
            <span>{node.plannedHeadcount ?? '-'}</span>
          </Kv>
          <Actions>
            {isVacant ? (
              <Button data-testid="panel-assign" disabled={locked} onClick={() => onAssign?.(String(node.positionId))}>
                {t('orgChart.assign')}
              </Button>
            ) : node.assignmentId && (
              <Button variant="outline" data-testid="panel-unassign" disabled={locked}
                onClick={() => onUnassign?.(node.assignmentId)}>
                {t('orgChart.unassign')}
              </Button>
            )}
            <Button variant="outline" data-testid="panel-add-child" disabled={locked}
              onClick={() => onAddChild?.(String(node.positionId))}>
              {t('orgChart.addChild')}
            </Button>
            <Button variant="outline" data-testid="panel-duplicate" disabled={locked} onClick={() => onDuplicate?.(node)}>
              {t('orgChart.duplicate')}
            </Button>
            {node.parentPositionId && (
              <Button variant="outline" data-testid="panel-detach" disabled={locked}
                onClick={() => onDetach?.(String(node.positionId))}>
                {t('orgChart.detach')}
              </Button>
            )}
            <Button variant="danger" data-testid="panel-delete" disabled={locked}
              onClick={() => onDeletePosition?.(String(node.positionId))}>
              {t('orgChart.deletePosition')}
            </Button>
          </Actions>
        </>
      )}
    </Panel>
  );
}
